import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarCheck, MailOpen, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [offline, setOffline] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([
      axios.get(`${API}/bookings`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/contact`).then((r) => r.data).catch(() => null),
    ]).then(([b, c]) => {
      if (b === null && c === null) setOffline(true);
      setBookings((b || []).reverse());
      setEnquiries((c || []).reverse());
      setLoading(false);
    });
  }, []);

  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

  return (
    <div data-testid="admin-page" className="bg-[#FAF5EC] min-h-screen text-kiki-ink warm-spotlight">
      <header className="border-b border-kiki-line bg-[#FAF5EC]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link data-testid="admin-back-link" to="/" className="flex items-center gap-2.5 text-sm text-kiki-muted hover:text-kiki-gold transition-colors duration-300 font-outfit">
            <ArrowLeft className="w-4 h-4" />
            <img src="/logo.png" alt="Kiki Services" className="w-8 h-8 rounded-full object-cover border border-kiki-line" />
            <span className="font-display text-xl text-kiki-ink">Kiki<span className="italic text-kiki-gold">Services</span></span>
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-widest text-kiki-muted">Owner view</span>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Reveal><Eyebrow>Bookings & enquiries</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-kiki-ink mt-3">Inbox</h1>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="flex gap-3 mt-8">
            <button
              data-testid="admin-tab-bookings"
              onClick={() => setTab("bookings")}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-outfit font-semibold transition-colors duration-300 ${
                tab === "bookings" ? "bg-kiki-ink border-kiki-ink text-[#FAF5EC]" : "border-kiki-line text-kiki-ink hover:border-kiki-gold/50"
              }`}
            >
              <CalendarCheck className="w-4 h-4" /> Bookings ({bookings.length})
            </button>
            <button
              data-testid="admin-tab-enquiries"
              onClick={() => setTab("enquiries")}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-outfit font-semibold transition-colors duration-300 ${
                tab === "enquiries" ? "bg-kiki-ink border-kiki-ink text-[#FAF5EC]" : "border-kiki-line text-kiki-ink hover:border-kiki-gold/50"
              }`}
            >
              <MailOpen className="w-4 h-4" /> Enquiries ({enquiries.length})
            </button>
          </div>
        </Reveal>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-kiki-gold" /></div>
        ) : offline ? (
          <div data-testid="admin-offline-note" className="mt-8 rounded-2xl border border-kiki-line bg-white p-8 text-center">
            <p className="font-display text-2xl text-kiki-ink">Inbox unavailable on this hosting</p>
            <p className="text-sm text-kiki-muted font-light mt-2">
              The bookings & enquiries inbox needs the hosted backend. On static GoDaddy hosting,
              booking and enquiry details arrive to you directly on WhatsApp instead.
            </p>
          </div>
        ) : tab === "bookings" ? (
          <div data-testid="admin-bookings-list" className="mt-8 space-y-4">
            {bookings.length === 0 && <p className="text-kiki-muted font-light">No bookings yet.</p>}
            {bookings.map((b, i) => (
              <div key={b.id} data-testid={`admin-booking-${i}`} className="rounded-2xl border border-kiki-line bg-white p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display text-2xl text-kiki-ink">{b.name}</p>
                  <p className="text-sm font-outfit font-semibold text-kiki-gold">{b.date} · {b.slot} IST</p>
                </div>
                <p className="text-sm text-kiki-muted mt-1">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
                {b.items?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {b.items.map((item) => (
                      <span key={item} className="text-xs font-outfit bg-kiki-surface border border-kiki-line rounded-full px-3 py-1 text-kiki-ink">{item}</span>
                    ))}
                  </div>
                )}
                {b.message && <p className="text-sm text-kiki-muted font-light mt-3 leading-relaxed">{b.message}</p>}
                <p className="text-[10px] font-mono uppercase tracking-widest text-kiki-muted/60 mt-4">{fmt(b.created_at)}</p>
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="admin-enquiries-list" className="mt-8 space-y-4">
            {enquiries.length === 0 && <p className="text-kiki-muted font-light">No enquiries yet.</p>}
            {enquiries.map((e, i) => (
              <div key={e.id} data-testid={`admin-enquiry-${i}`} className="rounded-2xl border border-kiki-line bg-white p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display text-2xl text-kiki-ink">{e.name}</p>
                  {e.pillar && <p className="text-xs font-mono uppercase tracking-widest text-kiki-gold">{e.pillar}</p>}
                </div>
                <p className="text-sm text-kiki-muted mt-1">{e.email}{e.company ? ` · ${e.company}` : ""}</p>
                <p className="text-sm text-kiki-muted font-light mt-3 leading-relaxed">{e.message}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-kiki-muted/60 mt-4">{fmt(e.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
